import { Timestamp } from '@bufbuild/protobuf';
import { ProjectDataTypes } from '@mono/proto-clients';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../../contexts/authContext';
import { projectDataClient } from '../../../../lib/connect/clients';
export function fromProto(m) {
    return {
        id: m.id,
        projectId: m.projectId,
        milestoneType: m.milestoneType,
        label: m.label,
        scheduledAt: m.scheduledAt?.toDate().toISOString() ?? '',
        source: m.source === 'pipeline' ? 'pipeline' : 'user',
        notes: m.notes || undefined,
        completedAt: m.completedAt?.toDate().toISOString(),
        createdAt: m.createdAt?.toDate().toISOString(),
        updatedAt: m.updatedAt?.toDate().toISOString()
    };
}
export function useProposalMilestones(projectId) {
    const { user } = useAuth();
    const companyId = user?.tenant_id ?? '';
    return useQuery({
        queryKey: [
            'proposal-milestones',
            companyId,
            projectId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listProposalMilestones({
                projectId,
                companyId
            });
            return resp.milestones.map(fromProto);
        },
        enabled: !!projectId && !!companyId
    });
}
export function useUpsertProposalMilestone(projectId) {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const companyId = user?.tenant_id ?? '';
            const m = new ProjectDataTypes.ProposalMilestone({
                id: input.id,
                tenantId: companyId,
                projectId,
                milestoneType: input.milestoneType,
                label: input.label,
                scheduledAt: Timestamp.fromDate(new Date(`${input.scheduledAt}T12:00:00Z`)),
                source: input.source,
                completedAt: input.completedAt ? Timestamp.fromDate(new Date(input.completedAt)) : undefined
            });
            const resp = await projectDataClient.upsertProposalMilestone({
                milestone: m
            });
            if (!resp.milestone) throw new Error('upsertProposalMilestone returned no milestone');
            return {
                milestone: fromProto(resp.milestone),
                companyId
            };
        },
        onSuccess: ({ companyId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'proposal-milestones',
                    companyId,
                    projectId
                ]
            });
        }
    });
}
export function useDeleteProposalMilestone(projectId) {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id)=>{
            const companyId = user?.tenant_id ?? '';
            await projectDataClient.deleteProposalMilestone({
                id,
                projectId,
                companyId
            });
            return companyId;
        },
        onSuccess: (companyId)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'proposal-milestones',
                    companyId,
                    projectId
                ]
            });
        }
    });
}
